import { useState, useRef, useEffect } from "react"
import { useNavigate } from 'react-router-dom';
import CancelIcon from '@mui/icons-material/Cancel';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
// import LocationOnIcon from '@mui/icons-material/LocationOn';
// import AccessTimeIcon from '@mui/icons-material/AccessTime';
// import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
// import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { ReactComponent as ResourcesIcon } from '../../icons/NavResourcesIconDark.svg';
import { ReactComponent as LocationIcon } from '../../icons/ResourceLocationIcon.svg';
import { ReactComponent as ClockIconFilled } from '../../icons/ClockIconFilled.svg';
// import GridViewIcon from '@mui/icons-material/GridView';
// import RefreshIcon from '@mui/icons-material/Refresh';
// import SortByAlphaIcon from '@mui/icons-material/SortByAlpha';
import SortIcon from '@mui/icons-material/Sort';
// import CheckBoxIcon from '@mui/icons-material/CheckBox';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
// import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { usStates, USStatesEnum, USStatesCounties } from "../../utils/enums"
import ResourcesMapView from "../../components/ResourcesMapView";
import { FilterType, SortByType, ResourceSchema, ResourceType, SettingsOptionsType } from "../../data/types";
import { useResource } from '../../contexts/appcontext';
import { capitalizeString } from '../../utils/helpers';
import { useLoadResourceOptions } from '../../hooks/apphooks';

const googleMapsApiKey = process.env.REACT_APP_GOOGLEMAPS_API_KEY_PROD;

type FilterOptionsProps = {
    resources?: ResourceSchema[]
    currentFilter: FilterType
    setCurrentFilter: React.Dispatch<React.SetStateAction<FilterType>>
    closeFilterSetting: () => void
}

const FilterOptions: React.FC<FilterOptionsProps> = ({resources, currentFilter, setCurrentFilter, closeFilterSetting}) => {
    const { setGlobalFilter } = useResource()
    const navigate = useNavigate();
    const { allOptions } = useLoadResourceOptions();
    const [zipcode, setZipcode] = useState<string>('');
    const [zipcodeError, setZipcodeError] = useState<string>('');
    const [isGeocodingZipcode, setIsGeocodingZipcode] = useState<boolean>(false);
    const zipcodeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    
    // State for expanded sections
    const [expandedSections, setExpandedSections] = useState<{
        resourceCategory: boolean;
        location: boolean;
        frequency: boolean;
        operatingHours: boolean;
        dates: boolean;
        priceRange: boolean;
        status: boolean;
        sortBy: boolean;
    }>({
        resourceCategory: false,
        location: false,
        frequency: false,
        operatingHours: false,
        dates: false,
        priceRange: false,
        status: false,
        sortBy: false,
    });

    const toggleSection = (section: keyof typeof expandedSections) => {
        setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
    };

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (zipcodeTimeoutRef.current) {
                clearTimeout(zipcodeTimeoutRef.current);
            }
        };
    }, []);

    const handleResourceCategoryChange = (resourceType: ResourceType) => {
        setCurrentFilter(prev => ({ ...prev, resourceType }));
        if (setGlobalFilter) {
            setGlobalFilter(prev => ({ ...prev, resourceType }));
        }
        navigate(`/${resourceType}${currentFilter.state ? `?state=${currentFilter.state}` : ''}`);
    };

    const handleStateChange = (value: string) => {
        setCurrentFilter(prev => ({ ...prev, state: value === 'nationwide' ? '' : value, county: '' }));
        if (setGlobalFilter) {
            setGlobalFilter(prev => ({ ...prev, state: value === 'nationwide' ? '' : value, county: '' }));
        }
        navigate(`/${currentFilter.resourceType}${(value && value !== 'nationwide') ? `?state=${value}` : ''}`);
        // Clear zipcode when state is manually changed
        setZipcode('');
        setZipcodeError('');
    };

    const handleCountyChange = (value: string) => {
        setCurrentFilter(prev => ({ ...prev, county: value === 'statewide' ? '' : value }));
        if (setGlobalFilter) {
            setGlobalFilter(prev => ({ ...prev, county: value === 'statewide' ? '' : value }));
        }
        // Clear zipcode when county is manually changed
        setZipcode('');
        setZipcodeError('');
    };

    const handleZipcodeChange = (value: string) => {
        // Only allow numeric input (US zipcodes are 5 digits, can have +4 extension)
        const numericValue = value.replace(/\D/g, '').slice(0, 9); // Max 9 digits (5+4)
        setZipcode(numericValue);
        setZipcodeError('');

        // Clear any pending geocoding
        if (zipcodeTimeoutRef.current) {
            clearTimeout(zipcodeTimeoutRef.current);
        }

        // Debounce geocoding - only geocode if we have 5 digits (valid US zipcode)
        if (numericValue.length === 5) {
            setIsGeocodingZipcode(true);
            zipcodeTimeoutRef.current = setTimeout(async () => {
                try {
                    const response = await fetch(
                        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(numericValue)}&key=${googleMapsApiKey}`
                    );
                    const data = await response.json();

                    if (data.results && data.results.length > 0) {
                        const addressComponents = data.results[0].address_components;
                        let foundState = '';
                        let foundCounty = '';

                        // Find state and county from address components
                        for (const component of addressComponents) {
                            if (component.types.includes('administrative_area_level_1')) {
                                foundState = component.short_name;
                            }
                            if (component.types.includes('administrative_area_level_2')) {
                                // Handle different county name formats
                                const countyName = component.long_name.replace(' County', '').replace(' Parish', '');
                                foundCounty = countyName;
                            }
                        }

                        if (foundState && usStates.includes(foundState)) {
                            // Update filter with state and county from zipcode
                            setCurrentFilter(prev => ({
                                ...prev,
                                state: foundState,
                                county: foundCounty || '',
                            }));
                            if (setGlobalFilter) {
                                setGlobalFilter(prev => ({
                                    ...prev,
                                    state: foundState,
                                    county: foundCounty || '',
                                }));
                            }
                            // Update URL
                            navigate(`/${currentFilter.resourceType}${foundState ? `?state=${foundState}` : ''}`);
                            setZipcodeError('');
                        } else {
                            setZipcodeError('Could not find state for this zipcode');
                        }
                    } else {
                        setZipcodeError('Invalid zipcode');
                    }
                } catch (error) {
                    console.error('Error geocoding zipcode:', error);
                    setZipcodeError('Error looking up zipcode');
                } finally {
                    setIsGeocodingZipcode(false);
                }
            }, 500); // 500ms debounce
        } else if (numericValue.length > 0 && numericValue.length < 5) {
            // Clear state/county if zipcode is incomplete
            setCurrentFilter(prev => ({ ...prev, state: '', county: '' }));
            if (setGlobalFilter) {
                setGlobalFilter(prev => ({ ...prev, state: '', county: '' }));
            }
            navigate(`/${currentFilter.resourceType}`);
        } else if (numericValue.length === 0) {
            // Clear everything if zipcode is cleared
            setZipcodeError('');
        }
    };

    const handleStatusChange = (value: string) => {
        setCurrentFilter(prev => ({ ...prev, active: value === 'active' }));
        if (setGlobalFilter) {
            setGlobalFilter(prev => ({ ...prev, active: value === 'active' }));
        }
    };

    const handleSortByChange = (value: SortByType) => {
        setCurrentFilter(prev => ({ ...prev, sortBy: value }));
        if (setGlobalFilter) {
            setGlobalFilter(prev => ({ ...prev, sortBy: value }));
        }
    };

    const clearAllFilters = () => {
        const clearedFilter: FilterType = {
            resourceType: currentFilter.resourceType,
            state: '',
            active: false,
            county: '',
            sortBy: undefined,
        };
        setCurrentFilter(clearedFilter);
        if (setGlobalFilter) {
            setGlobalFilter(clearedFilter);
        }
        // Clear zipcode when filters are cleared
        setZipcode('');
        setZipcodeError('');
        // Clear any pending geocoding
        if (zipcodeTimeoutRef.current) {
            clearTimeout(zipcodeTimeoutRef.current);
        }
    };

    const getResourceCategoryName = (resourceType: ResourceType) => {
        const option = allOptions.find((opt: SettingsOptionsType) => opt.path === resourceType);
        if (option) {
            // Return shorter name for display
            // const nameParts = option.name.split(' ');
            // if (nameParts.length > 2) {
            //     return nameParts.slice(0, 2).join(' ');
            // }
            return option.name;
        }
        return capitalizeString(resourceType);
    };

    const getLocationDisplay = () => {
        if (!currentFilter.state) return 'Select Location';
        const stateName = currentFilter.state === 'nationwide' ? 'All States' : USStatesEnum[currentFilter.state]?.fullName;
        const countyName = currentFilter.county ? currentFilter.county : '';
        return countyName ? `${countyName}, ${stateName}` : stateName;
    };

    const getStatusDisplay = () => {
        return currentFilter.active ? 'Active' : 'All';
    };

    const getSortByDisplay = () => {
        return currentFilter.sortBy ? capitalizeString(currentFilter.sortBy) : 'Select Sort By';
    };

    const sortOptions: SortByType[] = ['name', 'active', 'rating', 'county', 'state', 'zipcode', 'created'];
    const resourceCount = resources?.length || 0;

    return (
        <div className='flex flex-col h-full w-full' data-testid="filter-options">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-[18px] font-semibold text-[#1A1A1A] font-raleway">Find exactly what you need</h2>
                <button
                    onClick={closeFilterSetting}
                    className="w-8 h-8 border-0 rounded-full flex items-center justify-center hover:bg-[#E9E9E9] transition-colors"
                    aria-label="Close filter"
                >
                    <CancelIcon className="text-[#A1A1A1] h-5 w-5" />
                </button>
            </div>

            <div className="flex-1 overflow-y-auto pr-2 space-y-3">
                {/* Resource Category Section */}
                <div className={`rounded-xl border-[1px] border-[#E9E9E9]/70 transition-all overflow-hidden ${
                    expandedSections.resourceCategory 
                        ? 'bg-white min-h-[81px]' 
                        : 'bg-[#F8F8F8]/70 h-[81px]'
                }`}>
                    <button
                        onClick={() => toggleSection('resourceCategory')}
                        className={`w-full border-0 flex items-center justify-between p-4 h-[81px] rounded-lg transition-colors ${
                            expandedSections.resourceCategory 
                                ? 'hover:bg-[#F8F8F8]' 
                                : 'hover:bg-[#F0F0F0]'
                        }`}
                    >
                        <div className="flex items-center gap-3">
                            <div className="w-[32px] h-[32px] rounded-full bg-[#E6EBED]/90 flex items-center justify-center">
                                <ResourcesIcon className="text-[#013847] h-5 w-5" />
                            </div>
                            <div className="text-left">
                                <p className="font-medium font-raleway text-[#1A1A1A] text-sm">Resource Category</p>
                                <p className="text-xs font-open-sans text-[#666] mt-0.5">
                                    {getResourceCategoryName(currentFilter.resourceType)}
                                </p>
                            </div>
                        </div>
                        {expandedSections.resourceCategory ? (
                            <KeyboardArrowUpIcon className="text-[#A1A1A1] h-5 w-5" />
                        ) : (
                            <KeyboardArrowDownIcon className="text-[#A1A1A1] h-5 w-5" />
                        )}
                    </button>
                    {expandedSections.resourceCategory && (
                        <div className="px-4 pb-4 grid grid-cols-2 gap-3 my-1">
                            {allOptions.map((option: SettingsOptionsType) => {
                                const Icon = option.icon;
                                const isSelected = currentFilter.resourceType === option.path;
                                return (
                                    <button
                                        key={option.path}
                                        onClick={() => handleResourceCategoryChange(option.path as ResourceType)}
                                        className={`p-4 rounded-lg transition-all ${
                                            isSelected
                                                ? 'border-[#013847] bg-white border-2'
                                                : 'border-[#E9E9E9]/70 bg-white hover:border-[#D0D0D0]/70 border-[1px]'
                                        }`}
                                    >
                                        <div className="flex flex-col items-center gap-2">
                                            <Icon className={`h-8 w-8 ${isSelected ? 'text-[#013847]' : 'text-[#A1A1A1]'}`} />
                                            <span className={`text-[13px] font-raleway text-center ${isSelected ? 'text-[#013847] font-semibold' : 'text-[#1A1A1A]'}`}>
                                                {/* {option.name.split(' ').slice(0, 2).join(' ')} */}
                                                {option.name}
                                            </span>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Location Section */}
                <div className={`rounded-xl border-[1px] border-[#E9E9E9]/70 transition-all overflow-hidden ${
                    expandedSections.location 
                        ? 'bg-white min-h-[81px]' 
                        : 'bg-[#F8F8F8] h-[81px]'
                }`}>
                    <button
                        onClick={() => toggleSection('location')}
                        className={`w-full border-0 flex items-center justify-between p-4 h-[81px] rounded-lg transition-colors ${
                            expandedSections.location 
                                ? 'hover:bg-[#F8F8F8]' 
                                : 'hover:bg-[#F0F0F0]'
                        }`}
                    >
                        <div className="flex items-center gap-3">
                            <div className="w-[32px] h-[32px] rounded-full bg-[#E6EBED]/90 flex items-center justify-center">
                                <LocationIcon className="text-[#013847] h-5 w-5" />
                            </div>
                            <div className="text-left">
                                <p className="font-medium font-raleway text-[#1A1A1A] text-sm">Location</p>
                                <p className="text-xs font-open-sans text-[#666] mt-0.5">
                                    {getLocationDisplay()}
                                </p>
                            </div>
                        </div>
                        {expandedSections.location ? (
                            <KeyboardArrowUpIcon className="text-[#A1A1A1] h-5 w-5" />
                        ) : (
                            <KeyboardArrowDownIcon className="text-[#A1A1A1] h-5 w-5" />
                        )}
                    </button>
                    {expandedSections.location && (
                        <div className="px-4 pb-4 space-y-4 ">
                            {/* Map View - shown when state is selected */}
                            {currentFilter.state && (
                                <div className="rounded-lg overflow-hidden border border-[#E9E9E9]">
                                    <ResourcesMapView
                                        resources={resources}
                                        key={`${currentFilter.state}-${currentFilter.county}`}
                                        currentFilter={currentFilter}
                                        setCurrentFilter={setCurrentFilter}
                                        onStateChange={(newState) => {
                                            // Update URL when state changes from map panning
                                            navigate(`/${currentFilter.resourceType}${newState ? `?state=${newState}` : ''}`);
                                        }}
                                    />
                                </div>
                            )}
                            
                            {/* Zipcode Input */}
                            <div>
                                <label htmlFor="zipcode-input" className="block text-sm font-medium text-[#1A1A1A] mb-2">Zipcode (Optional)</label>
                                <input
                                    id="zipcode-input"
                                    type="text"
                                    inputMode="numeric"
                                    value={zipcode}
                                    onChange={(e) => handleZipcodeChange(e.target.value)}
                                    placeholder="Enter zipcode"
                                    className="w-full px-4 py-2.5 border border-[#E9E9E9] rounded-lg bg-white text-sm text-[#1A1A1A] focus:outline-none focus:ring-2 focus:ring-[#013847] focus:border-transparent"
                                />
                                {isGeocodingZipcode && (
                                    <p className="text-xs text-[#666] mt-1">Looking up zipcode...</p>
                                )}
                                {zipcodeError && (
                                    <p className="text-xs text-red-500 mt-1">{zipcodeError}</p>
                                )}
                                {zipcode.length === 5 && !zipcodeError && !isGeocodingZipcode && currentFilter.state && (
                                    <p className="text-xs text-green-600 mt-1">
                                        Found: {currentFilter.county ? `${currentFilter.county}, ` : ''}{USStatesEnum[currentFilter.state]?.fullName}
                                    </p>
                                )}
                            </div>

                            {/* Divider */}
                            <div className="flex items-center gap-3">
                                <div className="flex-1 border-t border-[#E9E9E9]"></div>
                                <span className="text-xs text-[#666] font-medium">OR</span>
                                <div className="flex-1 border-t border-[#E9E9E9]"></div>
                            </div>

                            {/* State and County Dropdowns */}
                            <div className="flex gap-4">
                                {/* State Dropdown */}
                                <div className="flex-1">
                                    <label htmlFor="state-select" className="block text-sm font-medium text-[#1A1A1A] mb-2">State/District</label>
                                    <select
                                        id="state-select"
                                        value={currentFilter.state || ''}
                                        onChange={(e) => handleStateChange(e.target.value)}
                                        className="w-full px-4 py-2.5 border border-[#E9E9E9] rounded-lg bg-white text-sm text-[#1A1A1A] focus:outline-none focus:ring-2 focus:ring-[#013847] focus:border-transparent"
                                    >
                                        <option value="">Select State</option>
                                        <option value="nationwide">All States</option>
                                        {usStates.map((state) => (
                                            <option key={state} value={state}>
                                                {USStatesEnum[state]?.fullName}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* County Dropdown */}
                                {currentFilter.state && USStatesCounties[currentFilter.state] && USStatesCounties[currentFilter.state].length > 0 && (
                                    <div className="flex-1">
                                        <label htmlFor="county-select" className="block text-sm font-medium text-[#1A1A1A] mb-2">
                                            {['DC'].includes(currentFilter.state) ? 'Ward' : 'County'}
                                        </label>
                                        <select
                                            id="county-select"
                                            value={currentFilter.county || ''}
                                            onChange={(e) => handleCountyChange(e.target.value)}
                                            className="w-full px-4 py-2.5 border border-[#E9E9E9] rounded-lg bg-white text-sm text-[#1A1A1A] focus:outline-none focus:ring-2 focus:ring-[#013847] focus:border-transparent"
                                        >
                                            <option value="">Select {['DC'].includes(currentFilter.state) ? 'Ward' : 'County'}</option>
                                            <option value="statewide">All {['DC'].includes(currentFilter.state) ? 'Wards' : 'Counties'}</option>
                                            {USStatesCounties[currentFilter.state].map((county) => (
                                                <option key={county} value={county}>
                                                    {county}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Available Status Section */}
                <div className={`rounded-xl border-[1px] border-[#E9E9E9]/70 transition-all overflow-hidden ${
                    expandedSections.status 
                        ? 'bg-white min-h-[81px]' 
                        : 'bg-[#F8F8F8] h-[81px]'
                }`}>
                    <button
                        onClick={() => toggleSection('status')}
                        className={`w-full border-0 flex items-center justify-between p-4 h-[81px] rounded-lg transition-colors ${
                            expandedSections.status 
                                ? 'hover:bg-[#F8F8F8]' 
                                : 'hover:bg-[#F0F0F0]'
                        }`}
                    >
                        <div className="flex items-center gap-3">
                            <div className="w-[32px] h-[32px] rounded-full bg-[#E6EBED]/90 flex items-center justify-center">
                                <ClockIconFilled className="text-[#013847] h-5 w-5" />
                            </div>
                            <div className="text-left">
                                <p className="font-medium font-raleway text-[#1A1A1A] text-sm">Availability</p>
                                <p className="text-xs font-open-sans text-[#666] mt-0.5">{getStatusDisplay()}</p>
                            </div>
                        </div>
                        {expandedSections.status ? (
                            <KeyboardArrowUpIcon className="text-[#A1A1A1] h-5 w-5" />
                        ) : (
                            <KeyboardArrowDownIcon className="text-[#A1A1A1] h-5 w-5" />
                        )}
                    </button>
                    {expandedSections.status && (
                        <div className="px-4 pb-4 space-y-2 my-1">
                            {['all', 'active'].map((status) => {
                                const isSelected = (status === 'active' && currentFilter.active) || (status === 'all' && !currentFilter.active);
                                return (
                                    <button
                                        key={status}
                                        onClick={() => handleStatusChange(status)}
                                        className={`w-full flex items-center justify-between p-3 rounded-lg  transition-all ${
                                            isSelected
                                                ? 'border-[#013847] bg-white border-2'
                                                : 'border-[#E9E9E9]/70 bg-white hover:border-[#D0D0D0] border-[1px]'
                                        }`}
                                    >
                                        <span className={`text-[13px] font-raleway text-left ${isSelected ? 'text-[#013847] font-semibold' : 'text-[#1A1A1A] font-normal'}`}>
                                            {capitalizeString(status)}
                                        </span>
                                        {isSelected ? (
                                            <RadioButtonCheckedIcon className="text-[#013847] rounded-full h-5 w-5" />
                                        ) : (
                                            <RadioButtonUncheckedIcon className="text-[#A1A1A1] h-5 w-5" />
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Sort By Section */}
                <div className={`rounded-xl border-[1px] border-[#E9E9E9]/70 transition-all overflow-hidden ${
                    expandedSections.sortBy 
                        ? 'bg-white min-h-[81px]' 
                        : 'bg-[#F8F8F8] h-[81px]'
                }`}>
                    <button
                        onClick={() => toggleSection('sortBy')}
                        className={`w-full border-0 flex items-center justify-between p-4 h-[81px] rounded-lg transition-colors ${
                            expandedSections.sortBy 
                                ? 'hover:bg-[#F8F8F8]' 
                                : 'hover:bg-[#F0F0F0]'
                        }`}
                    >
                        <div className="flex items-center gap-3">
                            <div className="w-[32px] h-[32px] rounded-full bg-[#E6EBED]/90 flex items-center justify-center">
                                <SortIcon style={{width: '20px', height: '20px'}} className="text-[#013847]" />
                            </div>
                            <div className="text-left">
                                <p className="font-medium font-raleway text-[#1A1A1A] text-sm">Sort By</p>
                                <p className="text-xs font-open-sans text-[#666] mt-0.5">{getSortByDisplay()}</p>
                            </div>
                        </div>
                        {expandedSections.sortBy ? (
                            <KeyboardArrowUpIcon className="text-[#A1A1A1] h-5 w-5" />
                        ) : (
                            <KeyboardArrowDownIcon className="text-[#A1A1A1] h-5 w-5" />
                        )}
                    </button>
                    {expandedSections.sortBy && (
                        <div className="px-4 pb-4 space-y-2 my-1">
                            {sortOptions.map((sortBy) => {
                                const isSelected = currentFilter.sortBy === sortBy;
                                return (
                                    <button
                                        key={sortBy}
                                        onClick={() => handleSortByChange(sortBy)}
                                        className={`w-full flex items-center justify-between p-3 rounded-lg  transition-all ${
                                            isSelected
                                                ? 'border-[#013847] bg-white border-2'
                                                : 'border-[#E9E9E9]/70 bg-white hover:border-[#D0D0D0] border-[1px]'
                                        }`}
                                    >
                                        <span className={`text-[13px] font-raleway text-left ${isSelected ? 'text-[#013847] font-semibold' : 'text-[#1A1A1A] font-normal'}`}>
                                            {capitalizeString(sortBy)}
                                        </span>
                                        {isSelected ? (
                                            <RadioButtonCheckedIcon className="text-[#013847] rounded-full h-5 w-5" />
                                        ) : (
                                            <RadioButtonUncheckedIcon className="text-[#A1A1A1] h-5 w-5" />
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between pt-4 mt-4 border-t border-[#E9E9E9]">
                <button
                    onClick={clearAllFilters}
                    className="text-[16px] font-semibold border-0 font-raleway text-[#666] hover:text-[#1A1A1A] transition-colors"
                >
                    Clear all
                </button>
                <button
                    onClick={closeFilterSetting}
                    className="px-6 py-2.5 bg-[#013847] border-0 text-white rounded-lg font-semibold text-[16px] hover:bg-[#012a35] transition-colors whitespace-nowrap"
                >
                    Show {resourceCount > 1 ? `${resourceCount-1}+` : ''} { resourceCount > 1 ? getResourceCategoryName(currentFilter.resourceType) : capitalizeString(currentFilter.resourceType)}
                    {/* Show {resourceCount > 0 ? `${resourceCount}+` : ''} {getResourceCategoryName(currentFilter.resourceType).replace('Local ', '').replace('Required ', '').replace(' & ', ' ')} */}
                </button>
            </div>
        </div>
    )
}

export default FilterOptions